import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Typography } from "@mui/material";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import { LabellerDetails } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { buildViewLabelsUrl } from "./utils";

export interface AccuracyEventRow {
  id: string;
  labellerEventStart: string;
  predictedLabel: string;
  actualLabel: string;
  iou: number;
}

interface Props {
  rows: AccuracyEventRow[];
  rowCount: number;
  loading: boolean;
  tenantName: string;
  location: string;
  labeller: LabellerDetails;
  formatLabel: (label: string) => string;
  showLabels: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}

const formatTimestamp = (iso: string): string =>
  dayjs.utc(iso).format("YYYY-MM-DD HH:mm:ss");

export const AccuracyEventsTable = ({
  rows,
  rowCount,
  loading,
  tenantName,
  location,
  labeller,
  formatLabel,
  showLabels,
  paginationModel,
  onPaginationModelChange,
}: Props) => {
  const { t } = useTenantTranslation();
  const formatPercent = useFormatNumber()(1, true);

  const columns = useMemo((): GridColDef<AccuracyEventRow>[] => {
    const baseColumns: GridColDef<AccuracyEventRow>[] = [
      {
        field: "labellerEventStart",
        headerName: t("labellerEventTime"),
        width: 200,
        sortable: false,
        valueFormatter: (value: string) => formatTimestamp(value),
      },
    ];

    const labelColumns: GridColDef<AccuracyEventRow>[] = showLabels
      ? [
          {
            field: "predictedLabel",
            headerName: t("predicted"),
            width: 160,
            sortable: false,
            valueFormatter: (value: string) => formatLabel(value),
          },
          {
            field: "actualLabel",
            headerName: t("actual"),
            width: 160,
            sortable: false,
            valueFormatter: (value: string) => formatLabel(value),
          },
        ]
      : [];

    const trailingColumns: GridColDef<AccuracyEventRow>[] = [
      {
        field: "iou",
        headerName: t("timingAccuracy"),
        type: "number",
        width: 120,
        sortable: false,
        valueFormatter: (value: number) => `${formatPercent(value * 100)}%`,
      },
      {
        field: "actions",
        headerName: t("openInViewLabels"),
        width: 160,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const url = buildViewLabelsUrl({
            tenantName,
            location,
            labeller,
            labellerEventStart: row.labellerEventStart,
          });
          return (
            <IconButton
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
              size="small"
              aria-label={t("openInViewLabels")}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    ];

    return [...baseColumns, ...labelColumns, ...trailingColumns];
  }, [
    t,
    formatLabel,
    formatPercent,
    tenantName,
    location,
    labeller,
    showLabels,
  ]);

  if (!loading && rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">{t("noEventsForCell")}</Typography>
      </Box>
    );
  }

  return (
    <HotkeyCompliantDataGrid
      columns={columns}
      rows={rows}
      loading={loading}
      paginationMode="server"
      rowCount={rowCount}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={[25, 50, 100]}
      slotProps={{
        loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
      }}
      rowHeight={40}
      columnHeaderHeight={44}
      disableRowSelectionOnClick
    />
  );
};
